import { ROUTES } from '#/config/routes';
import {
  rememberAsyncFilterLabels,
  resolveFromLabelCache,
} from '#/modules/commonComponents/table/asyncFilterLabelCache';
import {
  createAsyncMultiFilterMeta,
  createDateFilterMeta,
  createFixedMultiFilterMeta,
  createTextFilterMeta,
} from '#/modules/commonComponents/table/columnFilterMeta';
import { createDocumentNavLinksColumn } from '#/modules/commonComponents/table/tableColumnHelpers';
import TruncatedTextWithTooltip from '#/modules/commonComponents/table/TruncatedTextWithTooltip';
import SalesOrderStatusBadge from '#/modules/crmMgmt/salesOrder/components/SalesOrderStatusBadge';
import {
  readPlannedShipDateRangeFilter,
  resolvePlannedShipDateFilterChipDisplay,
} from '#/modules/crmMgmt/salesOrder/helper/salesOrderDateListFilter';
import { searchCustomerFilterOptions } from '#/modules/crmMgmt/customer/helper/searchCustomerFilterOptions';
import {
  SALES_ORDER_STATUS_FILTER_OPTIONS,
  canEditSalesOrderDraft,
} from '#/modules/crmMgmt/salesOrder/helper/salesOrderStatus';
import { formatDateOrDash } from '#/utils/date';
import { formatDocumentCurrencyLabel } from '#/utils/documentCurrencyLabel';

function displayDash(value) {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  return value;
}

function formatCustomerLabel(customer) {
  if (!customer) {
    return '-';
  }
  const label = [customer.code, customer.name].filter(Boolean).join(' - ');
  return label || '-';
}

export function buildSalesOrderColumns({
  filters = {},
  onFilterChange,
  onPlannedShipDateRangeChange,
  customerLabelCache,
  canShowEdit = (row) => canEditSalesOrderDraft(row.status),
} = {}) {
  return [
    createDocumentNavLinksColumn({
      getDetailPath: (row) => ROUTES.salesOrderDetail(row.id),
      getEditPath: (row) => ROUTES.salesOrderEdit(row.id),
      canShowEdit,
      size: 120,
      minSize: 120,
    }),
    {
      id: 'salesOrderNo',
      header: '銷貨單號',
      accessorKey: 'salesOrderNo',
      accessorFn: (row) => displayDash(row.salesOrderNo),
      size: 140,
      enableColumnFilter: true,
      meta: createTextFilterMeta({
        key: 'salesOrderNo',
        label: '銷貨單號',
        filterValue: filters.salesOrderNo,
        onChange: (value) => onFilterChange('salesOrderNo', value),
        placeholder: '搜尋銷貨單號',
      }),
    },
    {
      id: 'status',
      header: '狀態',
      accessorKey: 'status',
      size: 110,
      cell: ({ row }) => (
        row.original.status
          ? <SalesOrderStatusBadge status={row.original.status} size="sm" />
          : '-'
      ),
      enableColumnFilter: true,
      meta: {
        statusKind: 'document',
        ...createFixedMultiFilterMeta({
          key: 'status',
          label: '狀態',
          options: SALES_ORDER_STATUS_FILTER_OPTIONS,
          filterValue: filters.status,
          onChange: (value) => onFilterChange('status', value),
        }),
      },
    },
    {
      id: 'customerOrderNo',
      header: '客戶訂單號',
      accessorKey: 'customerOrderNo',
      accessorFn: (row) => displayDash(row.customerOrderNo),
      size: 140,
      enableSorting: false,
      enableColumnFilter: true,
      meta: createTextFilterMeta({
        key: 'customerOrderId',
        label: '客戶訂單',
        filterValue: filters.customerOrderId,
        onChange: (value) => onFilterChange('customerOrderId', value),
        placeholder: '客戶訂單 UUID',
      }),
    },
    {
      id: 'customer',
      header: '客戶',
      accessorFn: (row) => formatCustomerLabel(row.customer),
      size: 160,
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createAsyncMultiFilterMeta({
          key: 'customerId',
          label: '客戶',
          searchOptions: searchCustomerFilterOptions,
          filterValue: filters.customerId,
          onChange: (value) => onFilterChange('customerId', value),
          resolveFilterDisplay: (values) => (
            resolveFromLabelCache(customerLabelCache, values)
          ),
          rememberSelectedOptions: (options, values) => {
            rememberAsyncFilterLabels(customerLabelCache, options, values);
          },
        }),
      },
    },
    {
      id: 'plannedShipDate',
      header: '預交日',
      accessorKey: 'plannedShipDate',
      accessorFn: (row) => formatDateOrDash(row.plannedShipDate),
      size: 148,
      minSize: 148,
      enableColumnFilter: true,
      meta: createDateFilterMeta({
        key: 'plannedShipDateFrom',
        label: '預交日',
        mode: 'range',
        filterValue: readPlannedShipDateRangeFilter(filters),
        onChange: onPlannedShipDateRangeChange,
        placeholder: '西元年/月/日',
        resolveFilterDisplay: resolvePlannedShipDateFilterChipDisplay,
      }),
    },
    {
      id: 'currencyCode',
      header: '幣別',
      accessorFn: (row) => formatDocumentCurrencyLabel(row),
      size: 160,
      minSize: 140,
      enableSorting: false,
      enableColumnFilter: false,
    },
    {
      id: 'lineCount',
      header: '明細數',
      accessorKey: 'lineCount',
      accessorFn: (row) => displayDash(row.lineCount),
      size: 80,
      enableSorting: false,
    },
    {
      id: 'remark',
      header: '備註',
      accessorKey: 'remark',
      accessorFn: (row) => displayDash(row.remark),
      size: 180,
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'remark',
          label: '備註',
          filterValue: filters.remark,
          onChange: (value) => onFilterChange('remark', value),
          placeholder: '搜尋備註',
        }),
      },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip text={displayDash(row.original.remark)} />
      ),
    },
  ];
}
